<!-- Add by number: a request the other side must Accept. Screens role. -->
<template>
  <IonModal
    :is-open="isOpen"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    class="pm-scr-sheet pm-scr-sheet--auto"
    @will-present="onPresent"
    @did-present="focusNumber"
    @did-dismiss="onDismiss"
  >
    <form class="pm-scr-sheet-body" novalidate @submit.prevent="send.run()">
      <div class="pm-scr-sheet-head">
        <h2 class="section-title">Add by number</h2>
        <IonButton fill="clear" type="button" @click="$emit('close')">Cancel</IonButton>
      </div>

      <div class="pm-scr-field">
        <label class="pm-scr-label" :for="`${uid}-add-number`">Pager number</label>
        <input
          :id="`${uid}-add-number`"
          v-model="numberText"
          class="pm-scr-input pm-scr-input--lcd"
          type="tel"
          inputmode="numeric"
          autocomplete="off"
          placeholder="555-___-____"
          maxlength="14"
          :aria-invalid="numberError ? 'true' : 'false'"
          :aria-describedby="`${uid}-add-number-err`"
          @blur="validateNumber"
          @input="numberError = ''"
        >
        <p :id="`${uid}-add-number-err`" class="field-error" :hidden="!numberError" role="alert">{{ numberError }}</p>
      </div>

      <div class="pm-scr-field">
        <label class="pm-scr-label" :for="`${uid}-add-name`">Name <span class="pm-scr-muted">(optional)</span></label>
        <input
          :id="`${uid}-add-name`"
          v-model="name"
          class="pm-scr-input"
          type="text"
          autocomplete="off"
          autocapitalize="words"
          maxlength="24"
          placeholder="Sam"
          :aria-describedby="`${uid}-add-name-help`"
        >
        <p :id="`${uid}-add-name-help`" class="pm-scr-muted pm-scr-small">Save the number as someone — you'll see this instead of it. Leave it blank to use the name they chose.</p>
      </div>

      <div class="pm-scr-field">
        <label class="pm-scr-label" :for="`${uid}-add-first`">First page <span class="pm-scr-muted">(optional)</span></label>
        <textarea
          :id="`${uid}-add-first`"
          :value="firstPage"
          class="pm-scr-input pm-scr-textarea"
          rows="2"
          placeholder="HI IT'S ME"
          :aria-describedby="`${uid}-add-first-count`"
          @input="onFirstInput"
        />
        <p :id="`${uid}-add-first-count`" class="pm-scr-counter" :class="{ 'is-warn': firstCount >= 150 }">
          {{ firstCount }}/{{ MAX_GRAPHEMES }}
        </p>
      </div>

      <!-- Enter in a field submits; the visible button is an IonButton, which
           does not take part in implicit form submission on its own. -->
      <button type="submit" class="sr-only" tabindex="-1" aria-hidden="true" />
      <IonButton expand="block" type="button" :disabled="send.busy.value" @click="send.run()">
        {{ send.busy.value ? 'Sending…' : 'Send request' }}
      </IonButton>
      <p v-if="send.error.value" class="field-error" role="alert">{{ send.error.value }}</p>

      <p class="pm-scr-muted pm-scr-helper">
        Or share your invite link — anyone who opens it is added straight away.
      </p>
      <IonButton fill="clear" type="button" @click="$emit('share')">Share invite link</IonButton>
    </form>
  </IonModal>
</template>

<script setup>
import { userMessage } from './user-message.js';
import { ref, computed, useId } from 'vue';
import { IonModal, IonButton } from '@ionic/vue';
import { useAction, toast } from '../core/ui/index.js';
import { me, friendByNumber, friendName, addFriendByNumber } from '../store.js';
import { parseNumberInput } from '../pm/format.js';
import { countGraphemes, MAX_GRAPHEMES } from '../pm/text.js';

// Per-instance ids: Ionic keeps earlier pages in the DOM, so a fixed id
// would repeat and break label/for pairing.
const uid = `pm${useId()}`;

defineProps({ isOpen: { type: Boolean, default: false } });
const emit = defineEmits(['close', 'share', 'added']);

const numberText = ref('');
const name = ref('');
const firstPage = ref('');
const numberError = ref('');

const firstCount = computed(() => countGraphemes(firstPage.value));

function onFirstInput(e) {
  const next = e.target.value;
  if (countGraphemes(next) > MAX_GRAPHEMES) {
    // Block, don't truncate: put back what was there and say why.
    e.target.value = firstPage.value;
    toast(`Pages are ${MAX_GRAPHEMES} characters max`);
    return;
  }
  firstPage.value = next;
}

/** Returns the 10-digit number, or '' with numberError set. */
function validateNumber() {
  const raw = numberText.value.trim();
  if (!raw) { numberError.value = ''; return ''; }
  const n = parseNumberInput(raw);
  if (!n) {
    numberError.value = 'Enter a pager number like 555-382-9104.';
    return '';
  }
  if (n === me.value?.number) {
    numberError.value = "That's your own number.";
    return '';
  }
  const f = friendByNumber(n);
  if (f && f.status !== 'member') {
    const who = friendName(n);
    numberError.value = {
      accepted: `${who} is already a friend.`,
      'pending-out': `You've already asked ${who}. They need to accept.`,
      'pending-in': `${who} has already asked you — accept them under Pending.`,
      blocked: `You blocked ${who}. Unblock them in Settings first.`,
    }[f.status] ?? `${who} is already on your list.`;
    return '';
  }
  numberError.value = '';
  return n;
}

const send = useAction(async () => {
  if (!numberText.value.trim()) {
    numberError.value = 'Enter their pager number.';
    document.getElementById(`${uid}-add-number`)?.focus();
    return;
  }
  const n = validateNumber();
  if (!n) { document.getElementById(`${uid}-add-number`)?.focus(); return; }
  const r = await addFriendByNumber({
    number: n,
    name: name.value.trim(),
    firstPage: firstPage.value.trim(),
  });
  if (!r?.ok) throw new Error(userMessage(r, "Couldn't send the request. Check your connection and try again."));
  const status = r.friend?.status ?? friendByNumber(n)?.status;
  toast(status === 'accepted' ? `Paired with ${friendName(n)}` : `Request sent to ${friendName(n)}`);
  emit('added', n);
  emit('close');
}, { weight: 'pop' });

let opener = null;
function onPresent() {
  opener = document.activeElement;
  numberText.value = '';
  name.value = '';
  firstPage.value = '';
  numberError.value = '';
}
// The first field takes the cursor once the sheet has finished opening.
function focusNumber() {
  try { document.getElementById(`${uid}-add-number`)?.focus(); } catch { /* gone */ }
}
function onDismiss() {
  emit('close');
  const el = opener;
  opener = null;
  try { el?.focus?.(); } catch { /* gone */ }
}
</script>
